
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { Action, namespace } from "vuex-class";
import rest from '@/rest';
import router from '@/router';
import _ from "lodash";
import { List } from 'linq-collections';
import { vuex } from "@/store";
import { signalrEventBus } from '@/main';
import moment from "moment";

// components
import ReleaseProcessDlg from '@/components/BundleApproval/ReleaseProcessDlg.vue';
import ReviewProcessDlg from '@/components/BundleApproval/ReviewProcessDlg.vue';
import UserApprovalDlg from '@/components/BundleApproval/UserApprovalDlg.vue';
import ApprovalChatDlg from '@/components/BundleApproval/ApprovalChatDlg.vue';

// interfaces
import { ExCategory1 } from '@/interfaces/ExCategory1';
import { ExCategory2 } from '@/interfaces/ExCategory2';
import { ExCategory3 } from '@/interfaces/ExCategory3';
import { ParamDictionary } from '@/interfaces/ParamDictionary';
import { CategorySelectListsVm } from '@/interfaces/CategorySelectListsVm';
import { ParamGetExercises } from '@/interfaces/ParamGetExercises';
import { ExBundleGroupVM, ExBundleVM } from '@/interfaces/ExBundleGroupVM';
import { BundleApprovalVm } from '@/interfaces/BundleApprovalVm';
import { ExBundleAssignmentVM } from '@/interfaces/ExBundleAssignmentVM';

const auth = namespace("auth");
const ux = namespace("ux");
const globals = namespace("globals");
const approvalChat = namespace("approvalChat");

@Component({
  components: {
    ReleaseProcessDlg,
    ReviewProcessDlg,
    UserApprovalDlg,
    ApprovalChatDlg
  }
})
export default class CourseApprovalView extends Vue {
  @auth.Getter isAdmin: any;
  @auth.Getter isGroupAdmin: any;
  @auth.Getter isContentManager: any;
  @globals.Getter selectedBundle: any;
  @globals.Getter selectedCategory1!: ExCategory1 | null;
  @globals.Getter selectedCategory2!: ExCategory2 | null;
  @globals.Getter selectedCategory3!: ExCategory3 | null;

  @approvalChat.Action updateNewApprovalChatCount: any;

  showOnlyUnreleased = false;
  isLoadingBundleSuggestions = false;
  showReleaseProcessDlg = false;
  showReviewProcessDlg = false;
  showUserApprovalDlg = false;
  showApprovalChatDlg = false;

  category1Items: ExCategory1[] = [];
  category2Items: ExCategory2[] = [];
  category3Items: ExCategory3[] = [];
  exBundleGroups: ExBundleGroupVM[] = [];
  filteredBundleGroups: ExBundleGroupVM[] = [];
  selectedBundleGroupPanel: number | null = null;

  searchApproval: string = "";
  selectedCat1ForApproval = "-";
  selectedCat2ForApproval = "-";
  approvals: BundleApprovalVm[] = [];
  filteredApprovals: BundleApprovalVm[] = [];
  selectedApproval: BundleApprovalVm | null = null;

  searchReviews: string = "";
  filteredReviews: ExBundleVM[] = [];
  reviews: ExBundleVM[] = [];

  busy = false;
  tab = 0;

  async mounted() {
    vuex.globals.setUser(null);
    this.searchApproval = "";
    this.searchReviews = "";
    await this.loadCategories("cat1", "");
    await this.updateView();
    await this.$l3rnOnlineHub.ensureConnection();
  }

  created() {
    signalrEventBus.$on('updateApprovalChat', this.onUpdateApprovalChat);
  }

  beforeDestroy() {
    signalrEventBus.$off('updateApprovalChat', this.onUpdateApprovalChat);
  }

  async updateView() {
    this.getApprovalList();
    if (this.isAdmin || this.isContentManager) {
      this.getGroupedExBundles();
      this.getBundleReviewList();
    }
  }


  // bundle search
  bundleSuggestions: ExBundleVM[] = [];
  selectedBundleSuggestion: ExBundleVM | null = null;

  searchBundle: string = "";
  @Watch("searchBundle")
  async onSearchBundle(val) {
    if (this.selectedBundleSuggestion && (!val || val === this.selectedBundleSuggestion.name)) return;

    this.debouncedSearchBundle();
  }

  debouncedSearchBundle = _.debounce(this.loadBundleSuggestions, 700, { leading: true, trailing: true});
  async loadBundleSuggestions() {
    // console.log("loadBundleSuggestions", this.searchBundle);
    this.isLoadingBundleSuggestions = true
    this.bundleSuggestions = [];

    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["Search"] = this.searchBundle;
    params.dictionary!["OnlyUnreleased"] = this.showOnlyUnreleased.toString();
    params.dictionary!["ShowOnlyContentCreatorBundles"] = "false";
    params.dictionary!["ShowOnlyOldBundles"] = "false";

    await rest.url("userManagement/loadBundleSuggestions")
      .post(params)
      .then((response) => {
        this.bundleSuggestions = response;
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        this.isLoadingBundleSuggestions = false;
      });
  }

  async onBundleSuggestionCleared() {
    // console.log("onBundleSuggestionCleared");
    this.searchBundle = "";
    this.selectedBundleSuggestion = null;
    await this.loadBundleSuggestions();
  }

  async onBundleSuggestionSelected() {
    // console.log("onBundleSuggestionSelected");
    if (!this.selectedBundleSuggestion) return;

    let exCat1 = <ExCategory1>{ id: this.selectedBundleSuggestion?.categoryId1, name: this.selectedBundleSuggestion?.category1 };
    vuex.globals.setCategory1(exCat1);

    let exCat2 = <ExCategory2>{ id: this.selectedBundleSuggestion?.categoryId2, name: this.selectedBundleSuggestion?.category2 };
    vuex.globals.setCategory2(exCat2);

    await this.loadCategories("cat2", "");

    vuex.globals.setBundle(this.selectedBundleSuggestion);
    await this.getGroupedExBundles();
  }

  onUpdateApprovalChat() {
    if (this.showApprovalChatDlg)
      return;

    // console.log("CourseApprovalView.vue: onUpdateApprovalChat()");
    this.getApprovalList();
  }

  async cat1Changed(cat1: ExCategory1) {
    // console.log("grade changed, get exercises")
    vuex.globals.setCategory1(cat1);
    await this.onBundleSuggestionCleared();
    await this.loadCategories("cat1", "");
    await this.getGroupedExBundles();
  }

  async cat2Changed(cat2: ExCategory2) {
    // console.log("grade changed, get exercises")
    vuex.globals.setCategory2(cat2);
    await this.onBundleSuggestionCleared();
    await this.loadCategories("cat2", "");
    await this.getGroupedExBundles();
  }

  async loadCategories(changeEvent: string, selectGuid: string): Promise<void> {
    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["ChangeEvent"] = changeEvent;
    params.dictionary!["SelectGuid"] = selectGuid;
    // params.dictionary!["UserId"] = this.selectedUser.id!;
    // params.dictionary!["GroupId"] = this.selectedGroup.id.toString()!;
    params.dictionary!["Category1Id"] = this.selectedCategory1?.id.toString() ?? "0";
    params.dictionary!["Category2Id"] = this.selectedCategory2?.id.toString() ?? "0";
    params.dictionary!["Category3Id"] = this.selectedCategory3?.id.toString() ?? "0";

    await rest.url("exercises/loadAllCategories").post(params)
    .then((result: CategorySelectListsVm) => {
      this.category1Items = result.category1Items!;
      vuex.globals.setCategory1(result.selectedCat1Item);

      this.category2Items = result.category2Items!;
      vuex.globals.setCategory2(result.selectedCat2Item);

      this.category3Items = result.category3Items!;
      vuex.globals.setCategory3(result.selectedCat3Item);
    })
  }

  showExerciseBundleByApproval(approval: BundleApprovalVm) {
    // this.selectedApproval = approval;
    let assignmentVm = <ExBundleAssignmentVM><any>{
      id: 0, // not needed
      exBundleId: approval.exBundleId,
      exBundle: approval.exBundle,
      groupId: 0,
      certificateGranted: approval.exBundle?.hasTest ?? false, // to show fake certificate in test view
      certificateClosingDate: new Date(), // to show fake certificate in test view
      userId: null,
      enabled: true,
      writeResults: false
    }
    this.$store.commit('globals/setExBundleAssignment', assignmentVm);
    router.push("/exerciseBundle");
  }

  showExerciseBundleByBundle(bundle: ExBundleVM) {
    let assignmentVm = <ExBundleAssignmentVM>{
      id: 0, // not needed
      exBundleId: bundle.id,
      exBundle: bundle,
      userId: null,
      enabled: true,
      writeResults: false
    }
    this.$store.commit('globals/setExBundleAssignment', assignmentVm);
    router.push("/exerciseBundle");
  }

  get selectedCat1ItemName() {
    if (!this.selectedCategory1)
      return "-";
    return this.selectedCategory1.name;
  }

  get selectedCat2ItemName() {
    if (!this.selectedCategory2)
      return "-";
    return this.selectedCategory2.name;
  }

  get criticalReviewCount() {
    return new List(this.reviews).count(r => r.bundleReviewDueDateCritical);
  }

  get openApprovalsCount() {
    if (!this.approvals)
      return 0;

    return new List(this.approvals).count(a => !a.approved);
  }

  get cat1Items() {
    let cat1List = new List(this.approvals).select(x => x.exBundle?.category1).distinct().toArray();
    cat1List.unshift("-");
    return cat1List;
  }

  get cat2Items() {
    let approvalList = new List(this.approvals).select(x => x);
    if (this.selectedCat1ForApproval != "-"){
      approvalList = approvalList.where(x => x.exBundle?.category1 != null && x.exBundle.category1 == this.selectedCat1ForApproval);
    }

    let cat2Names = approvalList.select(x => x.exBundle?.category2).distinct().toArray();
    cat2Names.unshift("-");

    // Ensure that the selectedCat2ForApproval is still valid
    if (this.selectedCat2ForApproval != "-" && !cat2Names.includes(this.selectedCat2ForApproval)) {
      this.selectedCat2ForApproval = "-";
    }

    return cat2Names;
  }

  async getApprovalList() {
    this.approvals = [];
    this.filteredApprovals = [];
    this.approvals = await rest.url("bundleApproval/getApprovalList").get();
    await this.filterApprovals();
    await this.updateNewApprovalChatCount();
  }

  @Watch("searchApproval")
  async onSearchApproval() {
    await this.filterApprovals();
  }

  @Watch("selectedCat1ForApproval")
  async onSelectedCat1ForApprovalChange() {
    await this.filterApprovals();
  }

  @Watch("selectedCat2ForApproval")
  async onSelectedCat2ForApprovalChange() {
    await this.filterApprovals();
  }

  async filterApprovals() {
    // Workaround to correctly load placeholder images
    this.filteredApprovals = [];
    await this.$globalHelper.delay(5);

    if (!this.searchApproval) {
      this.filteredApprovals = this.approvals;
    } else {
      this.filteredApprovals = new List(this.approvals)
        .where(x => x.exBundle?.name?.toLowerCase().includes(this.searchApproval?.toLowerCase()) ?? false)
        .toArray();
    }

    if (this.selectedCat1ForApproval != "-") {
      this.filteredApprovals = new List(this.filteredApprovals).where(x => x.exBundle!.category1 == this.selectedCat1ForApproval).toArray();
    }

    if (this.selectedCat2ForApproval != "-") {
      this.filteredApprovals = new List(this.filteredApprovals).where(x => x.exBundle!.category2 == this.selectedCat2ForApproval).toArray();
    }
  }

  @Watch("searchReviews")
  async onSearchReviews() {
    await this.filterReviews();
  }

  async getBundleReviewList() {
    this.reviews = await rest.url("contentManager/getBundleReviewList").get() ?? [];
    // await this.updateNewApprovalChatCount();
    await this.filterReviews();
  }

  async filterReviews() {
    // Workaround to correctly load placeholder images
    this.filteredReviews = [];
    await this.$globalHelper.delay(5);

    if (!this.searchReviews) {
      this.filteredReviews = this.reviews;
    } else {
      this.filteredReviews = new List(this.reviews)
        .where(x => x.name?.toLowerCase().includes(this.searchReviews?.toLowerCase()) ?? false)
        .toArray();
    }
  }

  onShowOnlyUnreleasedChanged() {
    this.filterBundleGroups();
  }

  async getGroupedExBundles() {
    let param: ParamDictionary = { dictionary: {}};
    param.dictionary!["Category1Id"] = (this.selectedCategory1?.id ?? 0).toString();
    param.dictionary!["Category2Id"] =  (this.selectedCategory2?.id ?? 0).toString();
    param.dictionary!["AddSpecialFolders"] = "false";
    param.dictionary!["ShowOnlyContentCreatorBundles"] = "false";
    param.dictionary!["ShowOnlyOldBundles"] = "false";

    this.exBundleGroups = await rest.url("contentManager/getGroupedExBundles").post(param);
    this.filterBundleGroups();

    if (this.selectedBundle == null)
      return;

    // let catName = this.selectedBundle.category3;
    // if (catName == null) {
    //   this.selectedBundle = null;
    //   return;
    // }
    // let category = this.exBundleGroups.find(g => g.categoryName?.startsWith(catName!));
    // if (category) {
    //   this.selectedBundle = category?.bundles?.find(b => b.guid == this.selectedBundle?.guid) ?? null;
    // } else {
    //   this.selectedBundle = null;
    // }
    let group = new List(this.exBundleGroups).singleOrDefault(g => new List(g.bundles!).any(b => b.guid == this.selectedBundle.guid));
    vuex.globals.setBundle(group?.bundles?.find(b => b.guid == this.selectedBundle.guid) ?? null);
    if (group) {
      // open expansion panel
      let index = this.exBundleGroups.indexOf(group);
      this.selectedBundleGroupPanel = index;
    }
  }

  filterBundleGroups() {
    if (!this.showOnlyUnreleased) {
      this.filteredBundleGroups = this.exBundleGroups;
      return;
    }

    this.filteredBundleGroups = [];
    new List(this.exBundleGroups).forEach(g =>
      {
        let filteredBundles = new List(g.bundles!).where(b => !b.bundleIsReleased).toArray();
        // console.log(`filteredBundles for ${g.categoryName}: `, filteredBundles);
        this.filteredBundleGroups.push(<ExBundleGroupVM>{ categoryName: g.categoryName, bundles: filteredBundles });
      }
    );
  }

  onBundleRelease(bundle: ExBundleVM) {
    // this.selectedBundle = bundle;
    vuex.globals.setBundle(bundle);
    this.showReleaseProcessDlg = true;
  }

  onBundleReview(bundle: ExBundleVM) {
    // this.selectedBundle = bundle;
    vuex.globals.setBundle(bundle);
    this.showReviewProcessDlg = true;
  }

  async onUserApproval(approval: BundleApprovalVm) {
    this.selectedApproval = approval;
    this.showUserApprovalDlg = true;
  }

  onBundleApprovalChat(approval: BundleApprovalVm) {
    this.selectedApproval = approval;
    this.showApprovalChatDlg = true;
  }

  approvalDateToLocalDate(approval: BundleApprovalVm): string {
    if (!approval?.date)
      return "";

    // let date = moment(item?.finishedByAdminDate).toDate().toLocaleString([], {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'});
    let date = moment(approval?.date).toDate().toLocaleString([], {year: 'numeric', month:  '2-digit', day: '2-digit'});
    return date;
  }

  hasReleaseDueDate(bundle: ExBundleVM): boolean {
    return !!bundle?.bundleReleaseDueDate;
  }

  releaseDateToLocalDate(approval: BundleApprovalVm): string {
    if (!approval?.exBundle?.bundleReleasedByDate)
      return "";

    // let date = moment(item?.finishedByAdminDate).toDate().toLocaleString([], {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'});
    let date = moment(approval?.exBundle?.bundleReleasedByDate).toDate().toLocaleString([], {year: 'numeric', month:  '2-digit', day: '2-digit'});
    return date;
  }

  bundleReleaseDueDateToLocalDate(bundle: ExBundleVM): string {
    if (!bundle?.bundleReleaseDueDate)
      return "";

    // let date = moment(item?.finishedByAdminDate).toDate().toLocaleString([], {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'});
    let date = moment(bundle?.bundleReleaseDueDate).toDate().toLocaleString([], {year: 'numeric', month:  '2-digit', day: '2-digit'});
    return date;
  }

  bundleReleaseDateToLocalDate(bundle: ExBundleVM): string {
    if (!bundle?.bundleReleasedByDate)
      return "";

    // let date = moment(item?.finishedByAdminDate).toDate().toLocaleString([], {year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit'});
    let date = moment(bundle?.bundleReleasedByDate).toDate().toLocaleString([], {year: 'numeric', month:  '2-digit', day: '2-digit'});
    return date;
  }

  bundleReviewDueDateToLocalDate(bundle: ExBundleVM): string {
    if (!bundle?.bundleReviewDueDate)
      return "";

    let date = moment(bundle?.bundleReviewDueDate).toDate().toLocaleString([], {year: 'numeric', month:  '2-digit', day: '2-digit'});
    return date;
  }

  bundleReviewDateToLocalDate(bundle: ExBundleVM): string {
    if (!bundle?.bundleReviewedByDate)
      return "";

    let date = moment(bundle?.bundleReviewedByDate).toDate().toLocaleString([], {year: 'numeric', month:  '2-digit', day: '2-digit'});
    return date;
  }

  getBundleBorderStyle(bundle: ExBundleVM) {
    if (bundle.guid == this.selectedBundle?.guid)
      return `border: 2px solid ${this.$store.state.ux.colorAccent}; border-radius: 10px; padding-left: 5px !important; padding-right: 5px !important;`;
    return "";
  }

}
